import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { RealEstateMap, type Transaction } from '@/components/map/RealEstateMap';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Filter, Download, Info, TrendingUp, Map as MapIcon } from 'lucide-react';
import axiosInstance from '@/config/axios';
import { toast } from 'sonner';

export function RealEstateMapPage() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await axiosInstance.get('/api/map/transactions');
        setTransactions(response.data);
      } catch (error) {
        toast.error('Error al cargar transacciones');
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, []);

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-12rem)] flex flex-col gap-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-display font-black uppercase text-ink italic">Mapa Inmobiliario</h1>
            <p className="text-serif italic text-ink/60 mt-2">Explora transacciones reales y oferta de mercado.</p>
          </div>
          <div className="flex gap-4">
            <Button variant="outline" className="gap-2 border-2">
              <Filter className="h-4 w-4" />
              FILTROS AVANZADOS
            </Button>
            <Button className="gap-2">
              <Download className="h-4 w-4" />
              EXPORTAR CSV
            </Button>
          </div>
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 min-h-0">
          <div className="lg:col-span-8 relative">
            <RealEstateMap 
              transactions={transactions} 
              onMarkerClick={(t) => setSelectedTransaction(t)}
            />
            
            {/* Map Overlay Controls */}
            <div className="absolute top-4 left-4 z-10 w-72 space-y-4">
              <div className="brutalist-border-sm bg-parchment p-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/30" />
                  <Input placeholder="BUSCAR DIRECCIÓN..." className="pl-10 h-12 bg-transparent border-none text-xs font-black uppercase tracking-widest focus-visible:ring-0" />
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 flex flex-col gap-8 overflow-y-auto pr-2 custom-scrollbar">
            {selectedTransaction ? (
              <div className="brutalist-border bg-ink text-parchment p-8 space-y-8 animate-in fade-in slide-in-from-right-4">
                <div className="flex justify-between items-start">
                  <span className="bg-terracotta text-parchment px-3 py-1 text-[10px] font-black uppercase tracking-widest">Detalle Catastral</span>
                  <button onClick={() => setSelectedTransaction(null)} className="text-parchment/40 hover:text-parchment transition-colors">✕</button>
                </div>
                
                <div>
                  <h3 className="text-2xl font-display font-black uppercase italic mb-2">{selectedTransaction.location.address}</h3>
                  <p className="text-sm font-serif italic text-parchment/60">{selectedTransaction.location.city}, {selectedTransaction.location.country}</p>
                </div>

                <div className="grid grid-cols-2 gap-6 border-y border-parchment/10 py-8">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">Precio Venta</p>
                    <p className="text-xl font-display font-black text-terracotta">{selectedTransaction.currency} {selectedTransaction.price.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest opacity-40 mb-1">Superficie</p>
                    <p className="text-xl font-display font-black">{selectedTransaction.area} M²</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-xs font-black uppercase tracking-widest text-terracotta">Antecedentes CBR</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between text-xs font-serif italic">
                      <span className="opacity-60">ROL / Matrícula</span>
                      <span>{selectedTransaction.cadastralData?.rol || '1234-567'}</span>
                    </div>
                    <div className="flex justify-between text-xs font-serif italic">
                      <span className="opacity-60">Año Construcción</span>
                      <span>{selectedTransaction.cadastralData?.yearBuilt || '2015'}</span>
                    </div>
                    <div className="flex justify-between text-xs font-serif italic">
                      <span className="opacity-60">Avalúo Fiscal</span>
                      <span>UF 4.560</span>
                    </div>
                  </div>
                </div>

                <Button variant="outline" className="w-full border-parchment text-parchment hover:bg-parchment hover:text-ink">VER HISTORIAL COMPLETO</Button>
              </div>
            ) : (
              <div className="flex-1 brutalist-border bg-ink/5 p-12 flex flex-col items-center justify-center text-center">
                <MapIcon className="h-16 w-16 text-ink/10 mb-6" />
                <h3 className="text-xl font-display font-black uppercase italic text-ink/20">Selecciona una propiedad <br /> para ver detalles</h3>
              </div>
            )}

            <div className="brutalist-border bg-parchment p-8 space-y-6">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-6 w-6 text-terracotta" />
                <h3 className="text-lg font-display font-black uppercase italic">Análisis de Zona</h3>
              </div>
              <div className="space-y-4">
                <div className="p-4 bg-ink/5 space-y-1">
                  <p className="text-[10px] font-black uppercase tracking-widest opacity-40">Plusvalía Estimada</p>
                  <p className="text-2xl font-display font-black text-moss">+4.2% / año</p>
                </div>
                <div className="p-4 bg-ink/5 space-y-1">
                  <p className="text-[10px] font-black uppercase tracking-widest opacity-40">Precio Promedio M²</p>
                  <p className="text-2xl font-display font-black text-ink">UF 78.5</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
